import { FC, useState } from 'react';
import { Sidebar } from 'primereact/sidebar';
import { Button } from 'primereact/button';
import { Message } from 'primereact/message';
import { ProgressSpinner } from 'primereact/progressspinner';
import { TabView, TabPanel } from 'primereact/tabview';
import { RuleGroup } from '../waterfall-editor/RuleGroup';
import { FeeConfigGroupViewModel } from '../../models/fees';
import { SimpleExpression, FieldMetadata, OperatorInfo, MetadataContextType, LogicalOperator } from '../../models/expression';
import { convertToString } from '../../services/expressionService';
import { useExpressionFields, useExpressionOperators, useParsedExpression } from '../../hooks/useExpressionEditor';

export interface GroupLogicDrawerProps {
  visible: boolean;
  group: FeeConfigGroupViewModel | null;
  onClose: () => void;
  onSave: (qualifyingExpression: string | null) => Promise<void> | void;
}

const emptyExpression = (): SimpleExpression => ({
  expressions: [],
  logicalOperator: LogicalOperator.And,
  isNot: false,
});

const expressionHasConditions = (expression: SimpleExpression): boolean =>
  expression.expressions?.some(
    (item) => item.expressionGroup || (item.expression?.left && item.expression?.operator && item.expression?.right)
  ) ?? false;

/** Editor body, seeded once from the parsed expression via its initializer (remounted per group). */
const GroupLogicForm: FC<{
  initialExpression: SimpleExpression;
  fieldMetadata: FieldMetadata[];
  operators: OperatorInfo[];
  onClose: () => void;
  onSave: (qualifyingExpression: string | null) => Promise<void> | void;
}> = ({ initialExpression, fieldMetadata, operators, onClose, onSave }) => {
  const [expression, setExpression] = useState<SimpleExpression>(initialExpression);
  const [activeTab, setActiveTab] = useState(0);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSave = async () => {
    setIsSaving(true);
    setError(null);
    try {
      const qualifyingExpression = expressionHasConditions(expression) ? await convertToString(expression) : null;
      await onSave(qualifyingExpression);
      onClose();
    } catch (saveError) {
      setError(saveError instanceof Error ? saveError.message : 'Failed to save group logic.');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="flex flex-column gap-3">
      <label className="font-medium">Expression</label>
      <TabView activeIndex={activeTab} onTabChange={(event) => setActiveTab(event.index)}>
        <TabPanel header="Editor">
          <RuleGroup group={expression} level={0} onUpdate={setExpression} fieldMetadata={fieldMetadata} operators={operators} />
        </TabPanel>
        <TabPanel header="JSON">
          <pre className="bg-gray-100 p-3 border-round overflow-auto" style={{ maxHeight: '400px' }}>
            {JSON.stringify(expression, null, 2)}
          </pre>
        </TabPanel>
      </TabView>

      <Message
        severity="info"
        text="The rules engine evaluates the configurations in this group only when this expression is true. Sub-groups are evaluated only if this group also passes. Leave empty to always evaluate."
      />

      {error && <Message severity="error" text={error} />}

      <div className="flex justify-content-end gap-2 mt-2">
        <Button label="Cancel" className="p-button-text" onClick={onClose} disabled={isSaving} />
        <Button label="Save logic" icon="pi pi-check" onClick={handleSave} loading={isSaving} />
      </div>
    </div>
  );
};

/** Loads expression metadata + parses the group's saved expression, then renders the editor body. */
const GroupLogicEditor: FC<{
  group: FeeConfigGroupViewModel;
  onClose: () => void;
  onSave: (qualifyingExpression: string | null) => Promise<void> | void;
}> = ({ group, onClose, onSave }) => {
  const { data: fieldMetadata, isPending: fieldsPending, isError: fieldsError } = useExpressionFields({
    contextType: MetadataContextType.FeeInitial,
  });
  const { data: operators, isPending: operatorsPending } = useExpressionOperators();
  const { data: parsed, isPending: parsePending } = useParsedExpression({ expression: group.qualifyingExpression });

  const isParsing = !!group.qualifyingExpression?.trim() && parsePending;
  const isLoading = fieldsPending || operatorsPending || isParsing;

  if (isLoading) {
    return (
      <div className="flex flex-column align-items-center justify-content-center gap-2 py-6">
        <ProgressSpinner style={{ width: '40px', height: '40px' }} strokeWidth="4" />
        <span>Loading...</span>
      </div>
    );
  }

  if (fieldsError) {
    return <Message severity="error" text="Failed to load expression fields." />;
  }

  const initialExpression = parsed?.isValid && parsed.simpleExpression ? parsed.simpleExpression : emptyExpression();

  return (
    <GroupLogicForm
      initialExpression={initialExpression}
      fieldMetadata={fieldMetadata ?? []}
      operators={operators ?? []}
      onClose={onClose}
      onSave={onSave}
    />
  );
};

export const GroupLogicDrawer: FC<GroupLogicDrawerProps> = ({ visible, group, onClose, onSave }) => {
  return (
    <Sidebar
      visible={visible}
      position="right"
      onHide={onClose}
      style={{ width: '560px', maxWidth: '95vw' }}
      header={
        <div className="flex flex-column">
          <span className="text-xs font-semibold text-gray-500 uppercase">Group Logic</span>
          <span className="text-lg font-semibold">{group?.name}</span>
        </div>
      }
    >
      {visible && group && <GroupLogicEditor key={group.feeConfigGroupId} group={group} onClose={onClose} onSave={onSave} />}
    </Sidebar>
  );
};
